import axios from 'axios';
import { isInLoginGracePeriod } from './api';

// API client para o serviço de appointment (que contém as rotas de employee)
const appointmentAPI = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token
appointmentAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para lidar com erros
appointmentAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Não invalidar token se estivermos no período de graça após login
      if (isInLoginGracePeriod()) {
        console.warn('[EmployeeAPI] 401 durante período de graça após login, ignorando invalidação de token');
        return Promise.reject(error);
      }
      
      const isLoginPage = window.location.pathname === '/login';
      if (!isLoginPage) {
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
          localStorage.removeItem('token');
          localStorage.removeItem('default_company_id');
          window.dispatchEvent(new CustomEvent('auth:token-invalid'));
        }
      }
    }
    return Promise.reject(error);
  }
);

// Tipos
export interface Employee {
  id: string;
  email: string;
  name?: string;
  phone?: string;
  document?: string;
  birth_date?: string;
  role: string;
  zone?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CheckAvailabilityRequest {
  employee_id: string;
  scheduled_date: string;
  duration: number;
  exclude_id?: string;
}

export interface Appointment {
  id: string;
  property_id: string;
  employee_id: string;
  scheduled_date: string;
  duration: number;
  price: number;
  status: string;
  special_instructions?: string;
  reminder_minutes: number;
  created_at: string;
  updated_at: string;
}

export interface EmployeeAvailability {
  employee_id: string;
  is_available: boolean;
  conflict_count: number;
  conflicts?: Appointment[];
}

export interface ValidateZoneRequest {
  employee_id: string;
  property_id: string;
}

export interface ZoneValidation {
  is_valid: boolean;
  employee_zone: string;
  property_zone: string;
  message: string;
}

export interface EmployeeWorkload {
  employee_id: string;
  employee_name: string;
  total_appointments: number;
  completed_count: number;
  scheduled_count: number;
  cancelled_count: number;
  total_hours: number;
  total_revenue: number;
  average_rating?: number;
}

export interface EmployeeDashboard {
  employee_id: string;
  employee_name: string;
  zone: string;
  is_active: boolean;
  total_appointments: number;
  upcoming_count: number;
  completed_count: number;
  cancelled_count: number;
  next_appointments: Appointment[];
  total_hours_this_week: number;
  total_hours_this_month: number;
  revenue_this_week: number;
  revenue_this_month: number;
  average_rating?: number;
  completion_rate: number;
}

// Serviços de API

/**
 * Lista todos os funcionários ativos
 */
export const listActiveEmployees = async (zone?: string): Promise<Employee[]> => {
  const params = zone ? { zone } : {};
  try {
    const response = await appointmentAPI.get('/employees', { params });
    // O endpoint pode retornar:
    // 1. Array direto (appointment-service): [{...}, {...}]
    // 2. Objeto com employees (employee-service): { employees: [...], total: ... }
    const data = response.data;
    
    console.log('📋 Response bruto de listActiveEmployees:', data);
    console.log('📋 Tipo:', typeof data, 'É array?', Array.isArray(data));
    
    if (Array.isArray(data)) {
      console.log('📋 Retornando array direto com', data.length, 'funcionários');
      return data;
    }
    
    // Se for objeto, tentar extrair o array employees
    if (data && typeof data === 'object') {
      if (Array.isArray(data.employees)) {
        console.log('📋 Retornando data.employees com', data.employees.length, 'funcionários');
        return data.employees;
      }
      // Se tiver outros campos que podem ser arrays
      if (Array.isArray(data.data)) {
        console.log('📋 Retornando data.data com', data.data.length, 'funcionários');
        return data.data;
      }
      console.warn('⚠️ Formato de resposta inesperado:', Object.keys(data));
    }
    
    console.warn('⚠️ Nenhum funcionário encontrado no formato esperado');
    return [];
  } catch (error: any) {
    console.error('❌ Erro ao buscar funcionários:', error);
    console.error('❌ Status:', error.response?.status);
    console.error('❌ Response:', error.response?.data);
    throw error;
  }
};

/**
 * Busca um funcionário pelo ID
 */
export const getEmployeeByID = async (employeeId: string): Promise<Employee> => {
  const response = await appointmentAPI.get(`/employees/${employeeId}`);
  return response.data;
};

/**
 * Verifica a disponibilidade de um funcionário
 */
export const checkEmployeeAvailability = async (
  request: CheckAvailabilityRequest
): Promise<EmployeeAvailability> => {
  const response = await appointmentAPI.post('/employees/check-availability', request);
  return response.data;
};

/**
 * Valida se o funcionário pode atender na zona da propriedade
 */
export const validateEmployeeZone = async (
  request: ValidateZoneRequest
): Promise<ZoneValidation> => {
  const response = await appointmentAPI.post('/employees/validate-zone', request);
  return response.data;
};

/**
 * Busca o dashboard de um funcionário
 */
export const getEmployeeDashboard = async (
  employeeId: string,
  startDate?: string,
  endDate?: string
): Promise<EmployeeDashboard> => {
  const params: any = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  
  const response = await appointmentAPI.get(`/employees/${employeeId}/dashboard`, { params });
  return response.data;
};

/**
 * Busca a carga de trabalho de um funcionário
 */
export const getEmployeeWorkload = async (
  employeeId: string,
  startDate?: string,
  endDate?: string
): Promise<EmployeeWorkload> => {
  const params: any = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  
  const response = await appointmentAPI.get(`/employees/${employeeId}/workload`, { params });
  return response.data;
};

/**
 * Busca a carga de trabalho de todos os funcionários
 */
export const getAllEmployeesWorkload = async (
  startDate?: string,
  endDate?: string
): Promise<EmployeeWorkload[]> => {
  const params: any = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  
  const response = await appointmentAPI.get('/employees/workload', { params });
  return response.data;
};

/**
 * Busca funcionários por nome ou email
 */
export const searchEmployees = async (query: string): Promise<Employee[]> => {
  try {
    const response = await appointmentAPI.get('/employees/search', {
      params: { q: query, page: 1, page_size: 20 }
    });
    const data = response.data;
    // A resposta pode vir como { employees: [...], total: ... } ou diretamente como array
    if (data.employees && Array.isArray(data.employees)) {
      return data.employees;
    }
    return Array.isArray(data) ? data : [];
  } catch (error: any) {
    console.error('Erro ao buscar funcionários:', error);
    // Se der erro 404, pode ser que o endpoint não exista ainda
    // Retornar array vazio para não quebrar a UI
    if (error.response?.status === 404) {
      console.warn('Endpoint /employees/search não encontrado. Verifique se o employee-service está rodando.');
      return [];
    }
    throw error;
  }
};

/**
 * Cria um novo funcionário
 */
export interface CreateEmployeeRequest {
  name: string;
  email: string;
  phone?: string;
  document?: string;
  zone?: string;
  is_third_party?: boolean;
}

export const createEmployee = async (data: CreateEmployeeRequest): Promise<Employee> => {
  const response = await appointmentAPI.post('/employees', data);
  // A resposta pode vir como { id, email, ... } ou { employee: {...} }
  if (response.data.employee) {
    return response.data.employee;
  }
  // Se não tiver employee, buscar o funcionário criado pelo ID
  if (response.data.id) {
    return await getEmployeeByID(response.data.id);
  }
  return response.data;
};

export default {
  listActiveEmployees,
  getEmployeeByID,
  checkEmployeeAvailability,
  validateEmployeeZone,
  getEmployeeDashboard,
  getEmployeeWorkload,
  getAllEmployeesWorkload,
  searchEmployees,
  createEmployee,
};

